import {Modal} from 'antd';
import {noop} from 'lodash';
import {ReactElement, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router';

import {Locale} from '../../../../provider/locale/localization';

type PropsType = {
    isRunning: boolean;
};

/**
 * Bulk generation is driven from the reviews page, so navigating away or closing the tab drops
 * everything still queued.
 *
 * The dialog is rendered from state rather than opened through `modal.confirm`, because a confirm
 * created that way cannot be closed again from code — and this one has to disappear on its own as
 * soon as the run ends.
 */
export function BulkAiExitGuard({isRunning}: PropsType): ReactElement | null {
    const history = useHistory();
    const [pendingPath, setPendingPath] = useState<string | null>(null);
    const unblockRef = useRef<VoidFunction>(noop);

    useEffect(() => {
        if (!isRunning) {
            setPendingPath(null);

            return noop;
        }

        function handleBeforeUnload(event: BeforeUnloadEvent) {
            event.preventDefault();
            // Chrome only shows its own prompt when returnValue is set.
            // eslint-disable-next-line no-param-reassign
            event.returnValue = '';
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        const unblock = history.block((location) => {
            // Filters and pagination only change the query string, and the run survives them.
            const isSamePage = location.pathname === history.location.pathname;

            if (!isSamePage) {
                setPendingPath(`${location.pathname}${location.search}`);
            }

            return isSamePage ? noop() : false;
        });

        unblockRef.current = unblock;

        return () => {
            unblock();
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [history, isRunning]);

    if (!pendingPath) {
        return null;
    }

    return (
        <Modal
            cancelText={<Locale stringKey="POPUP__BUTTON__CANCEL" />}
            okText={<Locale stringKey="POPUP__BUTTON__CONFIRM" />}
            onCancel={() => setPendingPath(null)}
            onOk={() => {
                setPendingPath(null);
                unblockRef.current();
                history.push(pendingPath);
            }}
            open
            title={<Locale stringKey="REVIEWS__BULK_AI__LEAVE_PAGE__TITLE" />}
        >
            <Locale stringKey="REVIEWS__BULK_AI__LEAVE_PAGE__DESCRIPTION" />
        </Modal>
    );
}
